// src/data/permissionCatalog.js
// Permission catalog for Tenant 178 — data-driven, no hardcoding in UI

export const permissionCategories = [
    { id: 'base', name: 'Base Access', order: 1, icon: '🔑' },
    { id: 'investigation', name: 'Investigation Tools', order: 2, icon: '🔍' },
    { id: 'bulk', name: 'Bulk Operations', order: 3, icon: '📦' },
    { id: 'search', name: 'Search & Lookup', order: 4, icon: '🔎' },
    { id: 'admin', name: 'Admin & Config', order: 5, icon: '⚙️' },
    { id: 'other', name: 'Other', order: 6, icon: '📁' },
];

// Risk type categories (used for grouping in permission selector)
export const riskTypeCategories = [
    { id: 'LRP', name: 'Low Risk Permissions (LRP)', icon: '🟢', order: 1 },
    { id: 'MRP', name: 'Medium Risk Permissions (MRP)', icon: '🔵', order: 2 },
    { id: 'HRP', name: 'High Risk Permissions (HRP)', icon: '🟡', order: 3 },
    { id: 'CRP', name: 'Critical Risk Permissions (CRP)', icon: '🔴', order: 4 },
];

// Approved permissions per team (from leadership approvals)
export const approvedPermissionsByTeam = {
    'poe_international': ['base_pi', 'create_investigations', 'peeknow_resolved', 'action_widget', 'case_locker', 'uow_history', 'secure_link_nemo', 'unfettered_search'],
    'training': ['base_pi', 'unfettered_search'],
    // Add more teams as they get onboarded
};

export const permissions = [
    {
        id: 'base_pi',
        name: 'Base PI',
        category: 'base',
        description: 'Grant access to Paragon Investigations (TRMS, tenant 178), read/write access to assigned use cases',
        permissionLink: 'https://permissions.amazon.com/a/team/Paragon%20Investigations%20-%20Base%20PI',
        approvalLevel: 'L7',
        riskType: 'LRP',
        regions: ['NA', 'EU', 'FE'],
        restrictions: null,
        tags: ['base'],
    },
    {
        id: 'action_widget',
        name: 'Action Widget',
        category: 'investigation',
        description: 'Allows to take the following actions on an Investigation: 1) Resolve and Annotate, 2) Transfer and Annotate, 3) Reply, Select Reason/Sub Reason and Annotate',
        permissionLink: 'https://permissions.amazon.com/a/team/PI-Security-Exception',
        approvalLevel: 'L7',
        riskType: 'HRP',
        regions: ['NA', 'EU', 'FE'],
        restrictions: null,
        tags: [],
    },
    {
        id: 'case_locker',
        name: 'Case Locker',
        category: 'investigation',
        description: 'Grants the option to lock cases outside for those directly assigned to a user. This access is limited to People Managers only',
        permissionLink: 'https://permissions.amazon.com/a/team/Paragon%20Investigations%20-%20Case%20Locker',
        approvalLevel: 'L7',
        riskType: 'MRP',
        regions: ['NA', 'EU', 'FE'],
        restrictions: null,
        tags: ['managers'],
    },
    {
        id: 'create_investigations',
        name: 'Create Investigations',
        category: 'investigation',
        description: 'Grants access to the create investigation button in Paragon Investigations',
        permissionLink: 'https://permissions.amazon.com/a/team/Tenant%20178%20-%20Create%20Investigation',
        approvalLevel: 'L7',
        riskType: 'MRP',
        regions: ['NA', 'EU', 'FE'],
        restrictions: null,
        tags: [],
    },
    {
        id: 'bulk_asin',
        name: 'Bulk ASIN',
        category: 'bulk',
        description: 'Group to manage access to bulk-asin portal on Paragon Investigations',
        permissionLink: 'https://permissions.amazon.com/a/team/PI-BulkAsin',
        approvalLevel: 'L7',
        riskType: 'HRP',
        regions: ['NA', 'EU', 'FE'],
        restrictions: null,
        tags: [],
    },
    {
        id: 'bulk_merchant',
        name: 'Bulk Merchant',
        category: 'bulk',
        description: 'Group to manage access to bulk-merchant portal on Paragon Investigations',
        permissionLink: 'https://permissions.amazon.com/a/team/PI-BulkMerchant',
        approvalLevel: 'L7',
        riskType: 'HRP',
        regions: ['NA', 'EU', 'FE'],
        restrictions: null,
        tags: [],
    },
    {
        id: 'bulk_email_vendor',
        name: 'Bulk Email Vendor',
        category: 'bulk',
        description: 'This LDAP provides the ability to Bulk Email Vendors on PI as it provides the role "Acme.ParagonInvestigations.PeekNow"',
        permissionLink: 'https://permissions.amazon.com/a/team/Paragon%20Investigations%20-%20Bulk%20Email%20Vendor',
        approvalLevel: 'L7',
        riskType: 'MRP',
        regions: ['NA', 'EU', 'FE'],
        restrictions: null,
        tags: [],
    },
    {
        id: 'peeknow',
        name: 'Peeknow',
        category: 'search',
        description: 'Grants PeekNow access globally',
        permissionLink: 'https://permissions.amazon.com/a/team/PI-PeekNowAccess',
        approvalLevel: 'L7',
        riskType: 'MRP',
        regions: ['NA', 'EU', 'FE'],
        restrictions: null,
        tags: [],
    },
    {
        id: 'peeknow_resolved',
        name: 'Peeknow Resolved Tasks',
        category: 'search',
        description: 'Allows users to use PeekNow button on resolved tasks',
        permissionLink: 'https://permissions.amazon.com/a/team/PI-PeekNow-ResolvedTaskAccess',
        approvalLevel: 'L7',
        riskType: 'MRP',
        regions: ['NA', 'EU', 'FE'],
        restrictions: null,
        tags: [],
    },
    {
        id: 'peeknow_resolved_case',
        name: 'PeekNow Resolved Case Access',
        category: 'search',
        description: 'Grants PeekNow access to resolved case data',
        permissionLink: 'https://permissions.amazon.com/a/team/PI-PeekNow-ResolvedCase',
        approvalLevel: 'L6',
        riskType: 'MRP',
        regions: ['NA', 'EU', 'FE'],
        restrictions: null,
        tags: [],
    },
    {
        id: 'uow_history',
        name: 'Tenant 178 - UoW History Widget',
        category: 'other',
        description: 'Grants access to UoW History Widget on Paragon Investigations',
        permissionLink: 'https://permissions.amazon.com/a/team/Tenant%20178%20-%20UoW%20History%20Widget',
        approvalLevel: 'L6',
        riskType: 'LRP',
        regions: ['NA', 'EU', 'FE'],
        restrictions: null,
        tags: [],
    },
    {
        id: 'secure_link_nemo',
        name: 'Tenant 178 - Secure Link - Nemo',
        category: 'search',
        description: 'Grants access to NEMO Secured Link Generator',
        permissionLink: 'https://permissions.amazon.com/a/team/Tenant%20178%20-%20Secure%20Link%20Creation%20-%20Search%20scopes',
        permissionLinks: [
            { name: 'Secure Link - Search Scopes', link: 'https://permissions.amazon.com/a/team/Tenant%20178%20-%20Secure%20Link%20Creation%20-%20Search%20scopes' },
            { name: 'Secure Link - Access', link: 'https://permissions.amazon.com/a/team/Tenant%20178%20-%20Secure%20Link%20Access' },
            { name: 'Secure Link - Cases', link: 'https://permissions.amazon.com/a/team/Tenant%20178%20-%20Secure%20Link%20Creation%20-%20Cases' },
            { name: 'Secure Link - Bindle', link: 'https://permissions.amazon.com/a/team/amzn1.abacus.team.fhi76mjc3q6olxfvsxcq' },
        ],
        approvalLevel: 'L7',
        riskType: 'MRP',
        regions: ['NA', 'EU', 'FE'],
        restrictions: null,
        tags: [],
    },
    {
        id: 'standalone_ccp_launch',
        name: 'Standalone CCP Launch',
        category: 'tools',
        description: 'Access to Standalone CCP Launch tool',
        permissionLink: 'https://permissions.amazon.com/a/team/amzn1.abacus.team.iwyk4b2de7x6eh2adcda',
        approvalLevel: 'L7',
        riskType: 'MRP',
        regions: ['NA', 'EU', 'FE'],
        restrictions: null,
        tags: [],
    },
    {
        id: 'ps_appeals_bulk_tools',
        name: 'PS Appeals Bulk Tools',
        category: 'bulk',
        description: 'Access to PS Appeals Bulk Tools',
        permissionLink: 'https://permissions.amazon.com/a/team/amzn1.abacus.team.4r3aw6e6v4rliwh4ne2q',
        approvalLevel: 'L7',
        riskType: 'MRP',
        regions: ['NA', 'EU', 'FE'],
        restrictions: null,
        tags: [],
    },
    {
        id: 'bulk_sam_action',
        name: 'Bulk SAM Action',
        category: 'bulk',
        description: 'Access to Bulk SAM Action within Paragon Investigation',
        permissionLink: 'https://permissions.amazon.com/a/team/Paragon-Investigations-BulkSAMAction',
        approvalLevel: 'L7',
        riskType: 'HRP',
        regions: ['NA', 'EU', 'FE'],
        restrictions: null,
        tags: [],
    },
    {
        id: 'bulk_operations',
        name: 'Bulk Operations',
        category: 'bulk',
        description: 'Tool provides access to bulk transfer/resolve/assign investigations',
        permissionLink: 'https://permissions.amazon.com/a/team/PI-Bulk-Operations',
        approvalLevel: 'L7',
        riskType: 'HRP',
        regions: ['NA', 'EU', 'FE'],
        restrictions: null,
        tags: [],
    },
    {
        id: 'beta_access',
        name: 'Beta Access',
        category: 'other',
        description: 'Grants base and UFS access within Beta environment only',
        permissionLink: 'https://permissions.amazon.com/a/team/amzn1.abacus.team.nalhepbrz4b4r6n64e5a',
        approvalLevel: 'L6',
        riskType: 'LRP',
        regions: ['NA', 'EU', 'FE'],
        restrictions: null,
        tags: [],
    },
    {
        id: 'unfettered_search',
        name: 'Tenant 178 Unfettered Search',
        category: 'search',
        description: 'Grants lookup search exception',
        permissionLink: 'https://permissions.amazon.com/a/team/Paragon%20178%20Lookup%20Search',
        approvalLevel: 'L8',
        riskType: 'CRP',
        regions: ['NA', 'EU', 'FE'],
        restrictions: 'L8 approval required, L8 above if on leave',
        tags: ['l8-required'],
    },
    {
        id: 'routing_rcc',
        name: 'Routing and RCC Permissions',
        category: 'admin',
        description: 'Grants access for WFM for routing config in Tenant 178',
        permissionLink: 'https://permissions.amazon.com/a/team/PI%20Bindle%20-%20Routing%20and%20RCC%20Permissions',
        approvalLevel: 'L7',
        riskType: 'LRP',
        regions: ['NA', 'EU', 'FE'],
        restrictions: 'Only for WFM/Capacity Planners',
        tags: [],
    },
    {
        id: 'routing_rcc_view',
        name: 'Routing and RCC Permissions - View Only',
        category: 'admin',
        description: 'Grants view access to Connect Hybrid\'s RCC page',
        permissionLink: 'https://permissions.amazon.com/a/team/PI%20Bindle%20-%20Routing%20and%20RCC%20Permissions%20-%20View%20Only',
        approvalLevel: 'L7',
        riskType: 'LRP',
        regions: ['NA', 'EU', 'FE'],
        restrictions: 'Only for Operations Managers',
        tags: [],
    },
    {
        id: 'qct_routing_admin',
        name: 'QCT and Routing Admin Access',
        category: 'admin',
        description: 'Grants Queue Config Tool, Routing Ruleset, Site Allocation and Routing Dimension access',
        permissionLink: 'https://permissions.amazon.com/a/team/PI%20Bindle%20-%20QCT%20and%20Routing%20Admin%20Access',
        approvalLevel: 'L7',
        riskType: 'LRP',
        regions: ['NA', 'EU', 'FE'],
        restrictions: 'Only for SDEs/TASA/REX team',
        tags: [],
    },
    {
        id: 'rcc_workplan_debug',
        name: 'RCC Workplan Mapping and Debug Access',
        category: 'admin',
        description: 'Grants access to RCC Workplan Mapping and Debug tools',
        permissionLink: 'https://permissions.amazon.com/a/team/amzn1.abacus.team.vd7lzcpsbxn5ttnxidyq',
        approvalLevel: 'L7',
        riskType: 'LRP',
        regions: ['NA', 'EU', 'FE'],
        restrictions: 'Only for Process Engineers/SDEs',
        tags: [],
    },
    {
        id: 'rcc_debug',
        name: 'RCC Debug Tool',
        category: 'admin',
        description: 'Team to access and manage the RCC Debug Tool',
        permissionLink: 'https://permissions.amazon.com/a/team/PI%20Bindle%20-%20RCC%20Debug%20Tool',
        approvalLevel: 'L7',
        riskType: 'LRP',
        regions: ['NA', 'EU', 'FE'],
        restrictions: 'Only for Process Engineers/SDEs',
        tags: [],
    },
    {
        id: 'admin_view_config',
        name: 'Tenant 178 - Admin View Config Access',
        category: 'admin',
        description: 'Grants access to the View Configuration/BuildItNow Tool in Tenant 178',
        permissionLink: 'https://permissions.amazon.com/a/team/Tenant%20178%20-%20Admin%20View%20Config%20access',
        approvalLevel: 'L7',
        riskType: 'LRP',
        regions: ['NA', 'EU', 'FE'],
        restrictions: 'Only for Process Engineers/SDEs',
        tags: [],
    },
    {
        id: 'auditor_central',
        name: 'Tenant 178 - Auditor Central Admin',
        category: 'admin',
        description: 'Grants full admin level access to Auditor Central including program and form creation',
        permissionLink: 'https://permissions.amazon.com/a/team/amzn1.abacus.team.qhs6gwwy4lopbh4cj4wq',
        approvalLevel: 'L7',
        riskType: 'LRP',
        regions: ['NA', 'EU', 'FE'],
        restrictions: 'Only for Auditors and SDEs',
        tags: [],
    },
];

// Helper: get permissions approved for a specific team
export const getApprovedPermissionsForTeam = (teamValue) => {
    const approvedIds = approvedPermissionsByTeam[teamValue] || [];
    return permissions.filter(p => approvedIds.includes(p.id));
};

// Helper: get approved permissions grouped by risk type
export const getApprovedByRiskType = (teamValue) => {
    const approved = getApprovedPermissionsForTeam(teamValue);
    const groups = {};
    riskTypeCategories.forEach(cat => {
        const permsInGroup = approved.filter(p => p.riskType === cat.id);
        if (permsInGroup.length > 0) {
            groups[cat.id] = permsInGroup;
        }
    });
    return groups;
};

// Helper: get permissions grouped by category
export const getPermissionsByCategory = () => {
    const grouped = {};
    permissionCategories.forEach(cat => {
        grouped[cat.id] = {
            ...cat,
            permissions: permissions.filter(p => p.category === cat.id)
        };
    });
    return grouped;
};

// Helper: get highest approval level from selected permissions (based on risk type)
// CRP → L8, HRP → L7, MRP/LRP → L6
export const getHighestApprovalLevel = (selectedIds) => {
    const selected = permissions.filter(p => selectedIds.includes(p.id));
    const riskTypes = [...new Set(selected.map(p => p.riskType || 'LRP'))];
    if (riskTypes.includes('CRP')) return 'L8';
    if (riskTypes.includes('HRP')) return 'L7';
    return 'L6';
};

// Helper: get required approval level for a specific risk type
export const getApprovalLevelForRiskType = (riskType) => {
    if (riskType === 'CRP') return 'L8';
    if (riskType === 'HRP') return 'L7';
    return 'L6'; // LRP, MRP
};

// Helper: check if L8 approval is needed (only CRP/UFS requires L8)
export const needsL8Approval = (selectedIds) => {
    return permissions.filter(p => selectedIds.includes(p.id)).some(p => p.riskType === 'CRP');
};

// Helper: get selected permission objects
export const getSelectedPermissions = (selectedIds) => {
    return permissions.filter(p => selectedIds.includes(p.id));
};

// Risk type configuration
export const riskTypeConfig = {
    LRP: { label: 'Low Risk (LRP)', color: '#166534', bg: '#dcfce7', border: '#bbf7d0', accessDays: 180, checks: ['basePI'] },
    MRP: { label: 'Medium Risk (MRP)', color: '#0073bb', bg: '#e0f2fe', border: '#7dd3fc', accessDays: 180, checks: ['basePI'] },
    HRP: { label: 'High Risk (HRP)', color: '#92400e', bg: '#fef3c7', border: '#fde68a', accessDays: 120, checks: ['basePI', 'inactivity'] },
    CRP: { label: 'Critical Risk (CRP/UFS)', color: '#d13212', bg: '#ffeef0', border: '#ffc1c8', accessDays: 120, checks: ['basePI', 'inactivity', 'jobLevel', 'searchActivity'] },
};

// Helper: group selected permissions by risk type
export const groupByRiskType = (selectedIds) => {
    const selected = permissions.filter(p => selectedIds.includes(p.id));
    const groups = {};
    selected.forEach(p => {
        const rt = p.riskType || 'LRP';
        if (!groups[rt]) groups[rt] = [];
        groups[rt].push(p);
    });
    return groups;
};

// Helper: get risk types present in selection
export const getSelectedRiskTypes = (selectedIds) => {
    const selected = permissions.filter(p => selectedIds.includes(p.id));
    return [...new Set(selected.map(p => p.riskType || 'LRP'))];
};

// Helper: check if CRP (UFS) is in selection
export const hasCRP = (selectedIds) => {
    return permissions.filter(p => selectedIds.includes(p.id)).some(p => p.riskType === 'CRP');
};

// ════════════════════════════════════════════
// ACCESS CYCLE WINDOWS (Approved Use Cases Only)
// ════════════════════════════════════════════
// HRP/CRP: 3 windows (Jan-Apr, May-Aug, Sep-Dec)
// LRP/MRP: 2 windows (Jan-Jun, Jul-Dec)
// Grace period: If within 30 days of window end, extend to next window

const ACCESS_WINDOWS = {
    HRP: [
        { start: [0, 1], end: [3, 30] },   // Jan 1 – Apr 30
        { start: [4, 1], end: [7, 31] },   // May 1 – Aug 31
        { start: [8, 1], end: [11, 31] },  // Sep 1 – Dec 31
    ],
    CRP: [
        { start: [0, 1], end: [3, 30] },   // Jan 1 – Apr 30
        { start: [4, 1], end: [7, 31] },   // May 1 – Aug 31
        { start: [8, 1], end: [11, 31] },  // Sep 1 – Dec 31
    ],
    LRP: [
        { start: [0, 1], end: [5, 30] },   // Jan 1 – Jun 30
        { start: [6, 1], end: [11, 31] },  // Jul 1 – Dec 31
    ],
    MRP: [
        { start: [0, 1], end: [5, 30] },   // Jan 1 – Jun 30
        { start: [6, 1], end: [11, 31] },  // Jul 1 – Dec 31
    ],
};

const GRACE_PERIOD_DAYS = 30;

const WINDOW_LABELS = {
    HRP: ['Jan–Apr', 'May–Aug', 'Sep–Dec'],
    CRP: ['Jan–Apr', 'May–Aug', 'Sep–Dec'],
    LRP: ['Jan–Jun', 'Jul–Dec'],
    MRP: ['Jan–Jun', 'Jul–Dec'],
};

export const getAccessCycleEndDate = (riskType, requestDate = new Date()) => {
    const windows = ACCESS_WINDOWS[riskType] || ACCESS_WINDOWS.LRP;
    const labels = WINDOW_LABELS[riskType] || WINDOW_LABELS.LRP;
    const year = requestDate.getFullYear();

    for (let i = 0; i < windows.length; i++) {
        const winEnd = new Date(year, windows[i].end[0], windows[i].end[1]);
        const winStart = new Date(year, windows[i].start[0], windows[i].start[1]);

        // Check if request date falls within this window
        if (requestDate >= winStart && requestDate <= winEnd) {
            // Calculate days remaining in this window
            const daysRemaining = Math.ceil((winEnd - requestDate) / (1000 * 60 * 60 * 24));

            if (daysRemaining <= GRACE_PERIOD_DAYS) {
                // Grace period: extend to next window
                const nextIdx = (i + 1) % windows.length;
                const nextYear = nextIdx === 0 ? year + 1 : year;
                const nextEnd = new Date(nextYear, windows[nextIdx].end[0], windows[nextIdx].end[1]);
                const nextLabel = labels[nextIdx];
                return {
                    endDate: nextEnd,
                    endDateFormatted: `${String(nextEnd.getMonth() + 1).padStart(2, '0')}/${String(nextEnd.getDate()).padStart(2, '0')}/${nextEnd.getFullYear()}`,
                    windowLabel: nextLabel,
                    currentWindowLabel: labels[i],
                    isGracePeriod: true,
                    daysRemaining,
                    message: `Your request is within 30 days of the current cycle (${labels[i]}). Access extended to next cycle: ${nextLabel} ${nextYear}.`
                };
            } else {
                // Normal: use current window end date
                return {
                    endDate: winEnd,
                    endDateFormatted: `${String(winEnd.getMonth() + 1).padStart(2, '0')}/${String(winEnd.getDate()).padStart(2, '0')}/${winEnd.getFullYear()}`,
                    windowLabel: labels[i],
                    currentWindowLabel: labels[i],
                    isGracePeriod: false,
                    daysRemaining,
                    message: `Current access cycle: ${labels[i]} ${year}. Access expires ${String(winEnd.getMonth() + 1).padStart(2, '0')}/${String(winEnd.getDate()).padStart(2, '0')}/${year}.`
                };
            }
        }
    }

    // Fallback (shouldn't happen) — use last window of the year
    const lastWin = windows[windows.length - 1];
    const fallbackEnd = new Date(year, lastWin.end[0], lastWin.end[1]);
    return {
        endDate: fallbackEnd,
        endDateFormatted: `${String(fallbackEnd.getMonth() + 1).padStart(2, '0')}/${String(fallbackEnd.getDate()).padStart(2, '0')}/${fallbackEnd.getFullYear()}`,
        windowLabel: labels[labels.length - 1],
        currentWindowLabel: labels[labels.length - 1],
        isGracePeriod: false,
        daysRemaining: Math.ceil((fallbackEnd - requestDate) / (1000 * 60 * 60 * 24)),
        message: `Access cycle: ${labels[labels.length - 1]} ${year}.`
    };
};
